import { BaseStateInformationPortalUrl } from '../Constants';

/** SharePoint anchor on each state SIP page for the Required Documents section. */
export const SIP_REQUIRED_DOCUMENTS_ANCHOR = 'PolicyDocuments';

/** Row from `/States/getStates` — SQL: StateName, PostalAbbv → camelCase via API client. */
export type SipStateLookup = {
  stateName?: string;
  postalAbbv?: string;
};

/** Transaction row from `/customer/getTransactions` — SQL: [State], HomeState → camelCase via API client. */
export type SipTransactionStateFields = {
  state?: string | null;
  homeState?: string | null;
};

const findStateNameByAbbrev = (abbrev: string, states: SipStateLookup[]): string | null => {
  const normalized = abbrev.trim().toUpperCase();
  if (!normalized) {
    return null;
  }

  const match = states.find((entry) => entry.postalAbbv?.trim().toUpperCase() === normalized);
  return match?.stateName?.trim() || null;
};

/**
 * Resolves the SharePoint state page filename stem (full state name).
 * Prefer transaction `state` (s.Name); fall back to `/States/getStates` via `homeState` abbrev.
 */
export const resolveStateInformationPortalPageName = (
  stateName?: string | null,
  homeStateAbbrev?: string | null,
  states: SipStateLookup[] = [],
): string | null => {
  const trimmedName = stateName?.trim();
  if (trimmedName) {
    return trimmedName;
  }

  if (!homeStateAbbrev?.trim()) {
    return null;
  }

  return findStateNameByAbbrev(homeStateAbbrev, states);
};

/**
 * Builds the SIP deep link for a transaction's home state Required Documents section.
 * Returns null when the state cannot be resolved.
 */
export const buildStateInformationPortalRequiredDocumentsUrl = (
  stateName?: string | null,
  homeStateAbbrev?: string | null,
  states: SipStateLookup[] = [],
): string | null => {
  const pageName = resolveStateInformationPortalPageName(stateName, homeStateAbbrev, states);
  if (!pageName) {
    return null;
  }

  const encodedPage = `${encodeURIComponent(pageName)}.aspx`;
  return `${BaseStateInformationPortalUrl}/${encodedPage}#${SIP_REQUIRED_DOCUMENTS_ANCHOR}`;
};

export const buildStateInformationPortalRequiredDocumentsUrlFromRow = (
  row: SipTransactionStateFields,
  states: SipStateLookup[] = [],
): string | null =>
  buildStateInformationPortalRequiredDocumentsUrl(row.state, row.homeState, states);
